import { START_MAILBOX_SESSION, STOP_MAILBOX_SESSION } from '@/api/mutations';
import { MAILBOXES } from '@/api/queries';
import { toaster } from '@/components/ui/toaster';
import { useMutation } from '@apollo/client';
import React from 'react';
import { IMailbox, IMailboxSession } from '../types';
import ProcessingProgressSwitch from './ProcessingProgressSwitch';

interface MailboxProcessingStatusProps {
    mailbox: IMailbox;
}

const MailboxProcessingStatus = ({ mailbox }: MailboxProcessingStatusProps) => {
    const { id, currentSession } = mailbox;

    const [ startMailboxSession ] = useMutation(START_MAILBOX_SESSION, {
        refetchQueries: [ MAILBOXES ]
    });
    const [ stopMailboxSession ] = useMutation(STOP_MAILBOX_SESSION, {
        refetchQueries: [ MAILBOXES ]
    });

    const handleStatusChange = (checked: boolean) => {
        if (checked) {
            startMailboxSession({ variables: { mailboxId: id } });
        } else {
            stopMailboxSession({ variables: { mailboxId: id } }).then((result) => {
                const data = result?.data?.stopMailboxSession;
                if (!data) return;

                if (data.__typename === "ErrorType") {
                    toaster.create({
                        title: "Cannot stop session",
                        description:
                            data.message ||
                            "The AI agent is still working. The session will end automatically when it's done.",
                        type: "error",
                        closable: true,
                    });
                    return;
                }

                if (data.__typename === "NotifyType") {
                    toaster.create({
                        title: "Stop requested",
                        description:
                            data.message ||
                            "The session will stop after the current work finishes.",
                        type: "warning",
                        closable: true,
                    });
                    return;
                }

                if (data.__typename === "SuccessType") {
                    toaster.create({
                        title: "Session stopped",
                        description: data.message || "The mailbox session has been stopped.",
                        type: "success",
                        closable: true,
                    });
                }
            });
        }
    }

    return (
        <ProcessingProgressSwitch
            value={currentSession?.processingPercentage ?? 0} 
            status={currentSession?.status ?? 'cancelled'}
            onStatusChange={handleStatusChange}
            title='Processing' 
            size='xs' 
        />
    )
}

export default MailboxProcessingStatus;